// src/theme/theme.js
import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    primary: {
      main: '#6B4423', // Rich walnut brown
      light: '#8B5A3C', // Lighter wood tone
      dark: '#4A2F1A', // Dark mahogany
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#D2691E', // Warm copper/bronze accent
      light: '#E08D4C', // Light bronze
      dark: '#A0522D', // Sienna brown
      contrastText: '#FFFFFF',
    },
    tertiary: {
      main: '#8B7355', // Natural wood grain color
      light: '#A68B6A',
      dark: '#5C4A3A',
    },
    accent: {
      orange: '#FF6B35', // Vibrant orange for CTAs
      gold: '#DAA520', // Gold accents
      cream: '#FFF8DC', // Cream white
    },
    error: {
      main: '#C62828',
      light: '#EF5350',
      dark: '#8E0000',
    },
    warning: {
      main: '#F9A825',
      light: '#FFD54F',
      dark: '#F57F17',
    },
    info: {
      main: '#5C4033', // Dark wood info color
      light: '#795548',
      dark: '#3E2723',
    },
    success: {
      main: '#2E7D32', // Forest green
      light: '#4CAF50',
      dark: '#1B5E20',
    },
    background: {
      default: '#FAF7F2', // Warm off-white
      paper: '#FFFFFF',
      dark: '#2C2416', // Dark wood background
    },
    text: {
      primary: '#2C2416', // Very dark brown
      secondary: '#5C4033', // Medium brown
      disabled: '#9E9E9E',
    },
    divider: 'rgba(107, 68, 35, 0.12)', // Light brown divider
  },
  typography: {
    fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '3rem',
      fontWeight: 700,
      letterSpacing: '-0.02em',
      color: '#2C2416',
    },
    h2: {
      fontSize: '2.5rem',
      fontWeight: 600,
      letterSpacing: '-0.01em',
      color: '#2C2416',
    },
    h3: {
      fontSize: '2rem',
      fontWeight: 600,
      color: '#2C2416',
    },
    h4: {
      fontSize: '1.75rem',
      fontWeight: 600,
      color: '#2C2416',
    },
    h5: {
      fontSize: '1.5rem',
      fontWeight: 500,
      color: '#2C2416',
    },
    h6: {
      fontSize: '1.25rem',
      fontWeight: 500,
      color: '#2C2416',
    },
    subtitle1: {
      fontSize: '1.125rem',
      fontWeight: 400,
      lineHeight: 1.75,
      color: '#5C4033',
    },
    subtitle2: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.57,
      color: '#5C4033',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.8,
      color: '#2C2416',
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
      color: '#5C4033',
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      letterSpacing: '0.02em',
    },
    caption: {
      fontSize: '0.75rem',
      color: '#5C4033',
    },
  },
  shape: {
    borderRadius: 8, // Slightly rounded corners for modern look
  },
  shadows: [
    'none',
    '0px 2px 4px rgba(107, 68, 35, 0.05)',
    '0px 3px 6px rgba(107, 68, 35, 0.07)',
    '0px 4px 8px rgba(107, 68, 35, 0.08)',
    '0px 5px 10px rgba(107, 68, 35, 0.09)',
    '0px 6px 12px rgba(107, 68, 35, 0.10)',
    '0px 7px 14px rgba(107, 68, 35, 0.11)',
    '0px 8px 16px rgba(107, 68, 35, 0.12)',
    '0px 9px 18px rgba(107, 68, 35, 0.13)',
    '0px 10px 20px rgba(107, 68, 35, 0.14)',
    '0px 11px 22px rgba(107, 68, 35, 0.15)',
    '0px 12px 24px rgba(107, 68, 35, 0.16)',
    '0px 13px 26px rgba(107, 68, 35, 0.17)',
    '0px 14px 28px rgba(107, 68, 35, 0.18)',
    '0px 15px 30px rgba(107, 68, 35, 0.19)',
    '0px 16px 32px rgba(107, 68, 35, 0.20)',
    '0px 17px 34px rgba(107, 68, 35, 0.21)',
    '0px 18px 36px rgba(107, 68, 35, 0.22)',
    '0px 19px 38px rgba(107, 68, 35, 0.23)',
    '0px 20px 40px rgba(107, 68, 35, 0.24)',
    '0px 21px 42px rgba(107, 68, 35, 0.25)',
    '0px 22px 44px rgba(107, 68, 35, 0.26)',
    '0px 23px 46px rgba(107, 68, 35, 0.27)',
    '0px 24px 48px rgba(107, 68, 35, 0.28)',
    '0px 25px 50px rgba(107, 68, 35, 0.29)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          padding: '12px 28px',
          fontSize: '1rem',
          fontWeight: 600,
          boxShadow: 'none',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0px 4px 12px rgba(107, 68, 35, 0.15)',
            transform: 'translateY(-2px)',
          },
        },
        contained: {
          backgroundColor: '#6B4423',
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: '#8B5A3C',
          },
        },
        containedSecondary: {
          backgroundColor: '#D2691E',
          '&:hover': {
            backgroundColor: '#E08D4C',
          },
        },
        outlined: {
          borderColor: '#6B4423',
          color: '#6B4423',
          borderWidth: '2px',
          '&:hover': {
            borderColor: '#8B5A3C',
            backgroundColor: 'rgba(107, 68, 35, 0.04)',
            borderWidth: '2px',
          },
        },
        text: {
          color: '#6B4423',
          '&:hover': {
            backgroundColor: 'rgba(107, 68, 35, 0.04)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0px 4px 12px rgba(107, 68, 35, 0.08)',
          borderRadius: 12,
          backgroundColor: '#FFFFFF',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0px 8px 24px rgba(107, 68, 35, 0.12)',
            transform: 'translateY(-4px)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          color: '#2C2416',
          boxShadow: '0px 2px 8px rgba(107, 68, 35, 0.08)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          borderRadius: 6,
        },
        colorPrimary: {
          backgroundColor: 'rgba(107, 68, 35, 0.12)',
          color: '#6B4423',
        },
        colorSecondary: {
          backgroundColor: 'rgba(210, 105, 30, 0.12)',
          color: '#D2691E',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: '0px 2px 8px rgba(107, 68, 35, 0.08)',
        },
        elevation2: {
          boxShadow: '0px 4px 12px rgba(107, 68, 35, 0.10)',
        },
        elevation3: {
          boxShadow: '0px 6px 16px rgba(107, 68, 35, 0.12)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: 'rgba(107, 68, 35, 0.23)',
              borderWidth: '1.5px',
            },
            '&:hover fieldset': {
              borderColor: '#8B5A3C',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#6B4423',
              borderWidth: '2px',
            },
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(107, 68, 35, 0.12)',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: '#FAF7F2',
          color: '#2C2416',
          fontWeight: 600,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: '#6B4423',
          '&:hover': {
            backgroundColor: 'rgba(107, 68, 35, 0.04)',
          },
        },
      },
    },
    MuiRating: {
      styleOverrides: {
        iconFilled: {
          color: '#DAA520', // Gold color for stars
        },
        iconHover: {
          color: '#FFD700', // Bright gold on hover
        },
      },
    },
  },
});

// Add custom gradient for hero sections
theme.heroGradient = 'linear-gradient(135deg, #6B4423 0%, #8B5A3C 50%, #D2691E 100%)';
theme.subtleGradient = 'linear-gradient(180deg, #FAF7F2 0%, #FFFFFF 100%)';

export default theme;